import { useId, type ReactNode } from "react";

/**
 * Titled group of fields inside a form card. Sections stack vertically and
 * are separated by a hairline (see `.form-section` in index.css) rather than
 * nested boxes, so the page reads as one document with clear headings.
 *
 * `description` is the place for the one-line "what this section is for"
 * copy that used to be a loose `<p className="text-xs text-brand-muted">`
 * as the first child; `aside` renders right-aligned in the header row
 * (e.g. a "Defaults applied" note or a small toggle).
 */
export function Section({
  title,
  description,
  aside,
  children,
}: {
  title: string;
  description?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="form-section">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h2 id={headingId} className="form-section-title">
            {title}
          </h2>
          {description && <p className="form-section-desc">{description}</p>}
        </div>
        {aside && <div className="shrink-0 text-xs text-brand-muted">{aside}</div>}
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}
